import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader, EmptyState, BadgePill } from '@/components/campus';
import {
  Building2,
  ImagePlus,
  Save,
  Loader2,
  Check,
  Instagram,
  Twitter,
  Globe,
  Link2,
  Users,
  Crown,
  Shield,
  Calendar,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Officer title display configuration
const OFFICER_TITLE_CONFIG = {
  president: { label: 'President', icon: Crown, color: 'text-yellow-400' },
  vice_president: { label: 'Vice President', icon: Shield, color: 'text-electric-ice-blue' },
  treasurer: { label: 'Treasurer', icon: Briefcase, color: 'text-cool-mint', unlocks: 'Unlocks: Create Dues' },
  secretary: { label: 'Secretary', icon: Calendar, color: 'text-soft-amber' },
  risk_manager: { label: 'Risk Manager', icon: Shield, color: 'text-signal-red', unlocks: 'Unlocks: Document Vault' },
  social_chair: { label: 'Social Chair', icon: Users, color: 'text-purple-400' },
  rush_chair: { label: 'Rush Chair', icon: Users, color: 'text-pink-400' },
};

// ==================== SAVING INDICATOR COMPONENT ====================
const SavingIndicator = ({ saving, saved }) => {
  if (saving) {
    return (
      <div className="flex items-center gap-1.5 text-electric-ice-blue">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-xs">Saving...</span>
      </div>
    );
  }
  if (saved) {
    return (
      <div className="flex items-center gap-1.5 text-cool-mint">
        <Check className="w-3.5 h-3.5" />
        <span className="text-xs">Saved</span>
      </div>
    );
  }
  return null;
};

// ==================== SECTION COMPONENT ====================
const SettingsSection = ({ title, description, icon: Icon, children }) => (
  <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] overflow-hidden">
    <div className="p-5 border-b border-white/[0.06]">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-electric-ice-blue" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-cloud-white">{title}</h3>
          {description && <p className="text-sm text-muted-text mt-0.5">{description}</p>}
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ==================== FORM INPUT WITH AUTO-SAVE ====================
const AutoSaveInput = ({ label, value, onChange, onSave, type = 'text', placeholder, multiline = false }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleBlur = async () => {
    if (localValue !== value) {
      setSaving(true);
      await onSave(localValue);
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-soft-silver">{label}</Label>
        <SavingIndicator saving={saving} saved={saved} />
      </div>
      <InputComponent
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="bg-obsidian border-white/[0.08] text-cloud-white"
      />
    </div>
  );
};

// ==================== OFFICER CARD COMPONENT ====================
const OfficerCard = ({ officer, availableTitles, onTitleChange }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleTitleChange = async (newTitle) => {
    setSaving(true);
    await onTitleChange(officer.membership_id, newTitle === 'none' ? null : newTitle);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const currentTitleConfig = officer.officer_title ? OFFICER_TITLE_CONFIG[officer.officer_title] : null;

  return (
    <div className="flex items-center justify-between p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
      <div className="flex items-center gap-4">
        {/* Avatar */}
        {officer.avatar_url ? (
          <img
            src={officer.avatar_url}
            alt={officer.display_name}
            className="w-12 h-12 rounded-full object-cover border-2 border-white/[0.1]"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-electric-ice-blue" />
          </div>
        )}

        {/* Info */}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-cloud-white">{officer.display_name}</p>
            <BadgePill variant={officer.role === 'owner' ? 'amber' : 'blue'} size="sm">
              {officer.role}
            </BadgePill>
          </div>
          <p className="text-sm text-soft-silver">{officer.email}</p>
          {currentTitleConfig?.unlocks && (
            <p className="text-xs text-cool-mint mt-1">{currentTitleConfig.unlocks}</p>
          )}
        </div>
      </div>

      {/* Title Selector */}
      <div className="flex items-center gap-3">
        <SavingIndicator saving={saving} saved={saved} />
        <Select
          value={officer.officer_title || 'none'}
          onValueChange={handleTitleChange}
          disabled={saving}
        >
          <SelectTrigger 
            className="w-48 bg-graphite border-white/[0.08] text-cloud-white"
            data-testid={`officer-title-select-${officer.membership_id}`}
          >
            <SelectValue placeholder="Select title" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            <SelectItem value="none" className="text-muted-text">
              No Title
            </SelectItem>
            {availableTitles.map((title) => {
              const config = OFFICER_TITLE_CONFIG[title];
              const TitleIcon = config?.icon || Users;
              return (
                <SelectItem key={title} value={title} className="text-cloud-white">
                  <div className="flex items-center gap-2">
                    <TitleIcon className={cn("w-4 h-4", config?.color)} />
                    {config?.label || title}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// ==================== MAIN CHAPTER SETTINGS PAGE ====================
export const ChapterSettings = () => {
  const { organization, session, refreshOrganization } = useAuth();
  const { hasOrgRole } = usePermissions();

  // Check permissions - owner only
  const isOwner = hasOrgRole(['owner']);

  // State
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Fetch chapter settings
  const fetchSettings = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/organizations/${organization.id}/chapter-settings`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      } else if (res.status === 403) {
        // Not owner - will show access denied
      } else {
        toast.error('Failed to load chapter settings');
      }
    } catch (error) {
      console.error('Failed to fetch chapter settings:', error);
      toast.error('Failed to load chapter settings');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Save organization field
  const saveOrgField = async (field, value) => {
    try {
      const res = await fetch(
        `${API_URL}/api/organizations/${organization.id}/chapter-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ [field]: value }),
        }
      );

      if (res.ok) {
        // Update local state
        setSettings((prev) => ({
          ...prev,
          organization: { ...prev.organization, [field]: value },
        }));
        // Refresh auth context
        if (refreshOrganization) refreshOrganization();
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save field:', error);
      toast.error('Failed to save');
    }
  };

  // Save social link
  const saveSocialLink = async (platform, value) => {
    const currentLinks = settings.organization.social_links || {};
    const newLinks = { ...currentLinks, [platform]: value };
    
    // Remove empty links
    if (!value) delete newLinks[platform];

    try {
      const res = await fetch(
        `${API_URL}/api/organizations/${organization.id}/chapter-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ social_links: newLinks }),
        }
      );

      if (res.ok) {
        setSettings((prev) => ({
          ...prev,
          organization: { ...prev.organization, social_links: newLinks },
        }));
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save social link:', error);
      toast.error('Failed to save');
    }
  };

  // Update officer title
  const updateOfficerTitle = async (membershipId, title) => {
    try {
      const res = await fetch(
        `${API_URL}/api/organizations/${organization.id}/officers/${membershipId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ officer_title: title }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state
        setSettings((prev) => ({
          ...prev,
          officers: prev.officers.map((o) =>
            o.membership_id === membershipId ? { ...o, officer_title: title } : o
          ),
        }));
        
        if (data.unlocked_permissions?.length > 0) {
          toast.success(`Title updated! Unlocked: ${data.unlocked_permissions.join(', ')}`);
        }
      } else {
        toast.error(data.detail || 'Failed to update title');
      }
    } catch (error) {
      console.error('Failed to update officer title:', error);
      toast.error('Failed to update title');
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);

    try {
      // For now, convert to data URL - in production, upload to storage
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target?.result;
        await saveOrgField('logo_url', dataUrl);
        setUploadingLogo(false);
        toast.success('Logo updated');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  // Access denied
  if (!isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="Only organization owners can manage chapter settings."
        />
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading chapter settings...</p>
        </div>
      </div>
    );
  }

  // No settings loaded
  if (!settings) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="alert-circle"
          title="Failed to Load"
          description="Could not load chapter settings. Please try again."
          actionLabel="Retry"
          onAction={fetchSettings}
        />
      </div>
    );
  }

  const { organization: org, officers, available_titles } = settings;

  return (
    <div className="space-y-6" data-testid="chapter-settings-page">
      <PageHeader
        title="Chapter Settings"
        subtitle="Manage organization profile and officer assignments"
        icon={Building2}
      />

      {/* Organization Info Section */}
      <SettingsSection
        title="Organization Profile"
        description="Basic information visible to members"
        icon={Building2}
      >
        <div className="space-y-6">
          {/* Logo */}
          <div className="space-y-3">
            <Label className="text-soft-silver">Chapter Logo</Label>
            <div className="flex items-center gap-4">
              {org.logo_url ? (
                <img
                  src={org.logo_url}
                  alt={org.name}
                  className="w-20 h-20 rounded-xl object-cover border border-white/[0.1]"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center border border-white/[0.08]">
                  <Building2 className="w-8 h-8 text-electric-ice-blue" />
                </div>
              )}
              <div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
                    disabled={uploadingLogo}
                    asChild
                  >
                    <span>
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-4 h-4 mr-2" />
                          Change Logo
                        </>
                      )}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-text mt-2">Recommended: 200x200px, PNG or JPG</p>
              </div>
            </div>
          </div>

          {/* Name */}
          <AutoSaveInput
            label="Organization Name"
            value={org.name}
            onSave={(v) => saveOrgField('name', v)}
            placeholder="Enter organization name"
          />

          {/* Description */}
          <AutoSaveInput
            label="Description"
            value={org.description}
            onSave={(v) => saveOrgField('description', v)}
            placeholder="Brief description of your organization"
            multiline
          />
        </div>
      </SettingsSection>

      {/* Social Links Section */}
      <SettingsSection
        title="Social Links"
        description="Connect your social media profiles"
        icon={Link2}
      >
        <div className="space-y-4">
          <AutoSaveInput
            label="Website"
            value={org.social_links?.website || ''}
            onSave={(v) => saveSocialLink('website', v)}
            placeholder="https://yourwebsite.com"
            type="url"
          />
          <AutoSaveInput
            label="Instagram"
            value={org.social_links?.instagram || ''}
            onSave={(v) => saveSocialLink('instagram', v)}
            placeholder="@yourhandle"
          />
          <AutoSaveInput
            label="Twitter"
            value={org.social_links?.twitter || ''}
            onSave={(v) => saveSocialLink('twitter', v)}
            placeholder="@yourhandle"
          />
        </div>
      </SettingsSection>

      {/* Officer Management Section */}
      <SettingsSection
        title="Officer Titles"
        description="Assign officer positions to leadership. Treasurer unlocks Dues creation, Risk Manager unlocks Document Vault."
        icon={Crown}
      >
        <div className="space-y-4">
          {officers.length === 0 ? (
            <p className="text-muted-text text-sm text-center py-8">
              No officers found. Add admins or event managers to assign titles.
            </p>
          ) : (
            officers.map((officer) => (
              <OfficerCard
                key={officer.membership_id}
                officer={officer}
                availableTitles={available_titles}
                onTitleChange={updateOfficerTitle}
              />
            ))
          )}
        </div>
      </SettingsSection>
    </div>
  );
};

export default ChapterSettings;
